
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { cn } from '@/lib/utils';

interface AdaptiveTextProps {
  children?: React.ReactNode;
  maxLength?: number;
  baseSize?: 'sm' | 'md' | 'lg' | 'xl' | 'auto';
  className?: string;
  weight?: 'normal' | 'medium' | 'semibold' | 'bold';
  shadow?: boolean;
  autoResize?: boolean;
  minSize?: string;
  maxSize?: string;
  containerHeight?: number;
  dangerouslySetInnerHTML?: { __html: string };
}

export const AdaptiveText: React.FC<AdaptiveTextProps> = ({
  children,
  maxLength = 100,
  baseSize = 'auto',
  className,
  weight = 'normal',
  shadow = true,
  autoResize = true,
  minSize = '8rem',
  maxSize = '50rem',
  containerHeight,
  dangerouslySetInnerHTML
}) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [fontSize, setFontSize] = useState<string>('');
  const [isCalculating, setIsCalculating] = useState(false);
  const resizeTimeoutRef = useRef<NodeJS.Timeout>();
  const lastSizeRef = useRef<string>('');
  
  // Use dangerouslySetInnerHTML content for length calculation if provided
  const textContent = dangerouslySetInnerHTML?.__html || (typeof children === 'string' ? children : '');
  const textLength = textContent.length;
  
  // Calculate size based on text length and base size - with massive default sizes for display boards
  const getSizeClasses = () => {
    if (baseSize !== 'auto') {
      const baseSizes = {
        sm: 'text-[8rem] sm:text-[10rem] md:text-[12rem]',
        md: 'text-[10rem] sm:text-[12rem] md:text-[15rem] lg:text-[20rem]',
        lg: 'text-[12rem] sm:text-[15rem] md:text-[20rem] lg:text-[25rem] xl:text-[30rem]',
        xl: 'text-[15rem] sm:text-[20rem] md:text-[25rem] lg:text-[30rem] xl:text-[40rem]'
      };
      return baseSizes[baseSize];
    }

    // Auto-sizing based on content length with massive font sizes for display boards
    if (textLength > maxLength * 3) {
      return 'text-[8rem] sm:text-[10rem] md:text-[12rem]';
    } else if (textLength > maxLength * 2) {
      return 'text-[10rem] sm:text-[12rem] md:text-[15rem]';
    } else if (textLength > maxLength * 1.5) {
      return 'text-[12rem] sm:text-[15rem] md:text-[20rem]';
    } else if (textLength > maxLength) {
      return 'text-[15rem] sm:text-[20rem] md:text-[25rem]';
    } else if (textLength > maxLength * 0.7) {
      return 'text-[20rem] sm:text-[25rem] md:text-[30rem]';
    } else if (textLength > maxLength * 0.4) {
      return 'text-[25rem] sm:text-[30rem] md:text-[35rem]';
    } else {
      return 'text-[30rem] sm:text-[35rem] md:text-[40rem]';
    }
  };

  // Enhanced font size adjustment with stability improvements
  const adjustFontSize = useCallback(() => {
    if (!autoResize || !textRef.current || isCalculating) return;

    const element = textRef.current;
    const container = element.parentElement;
    if (!container) return;

    setIsCalculating(true);

    // Clear any existing timeout
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    // Longer debounce for stability
    resizeTimeoutRef.current = setTimeout(() => {
      let currentSize = 800; // Start with massive size for display boards (50rem = 800px)
      const maxAllowedSize = parseInt(maxSize.replace('rem', '')) * 16;
      const minAllowedSize = parseInt(minSize.replace('rem', '')) * 16;

      // Set initial size
      element.style.fontSize = `${currentSize}px`;

      // Check if content overflows and adjust down
      let iterationCount = 0;
      const maxIterations = 30; // Reduced iterations for stability
      
      while (
        (element.scrollHeight > container.clientHeight || 
         element.scrollWidth > container.clientWidth) && 
        currentSize > minAllowedSize &&
        iterationCount < maxIterations
      ) {
        currentSize -= 16; // Larger steps for faster convergence
        element.style.fontSize = `${currentSize}px`;
        iterationCount++;
        
        if (currentSize <= minAllowedSize) break;
      }

      // Only try to increase if we have significant extra space
      iterationCount = 0;
      while (
        element.scrollHeight < container.clientHeight * 0.7 && 
        element.scrollWidth < container.clientWidth * 0.8 &&
        currentSize < maxAllowedSize &&
        iterationCount < maxIterations
      ) {
        const testSize = currentSize + 16;
        element.style.fontSize = `${testSize}px`;
        
        if (element.scrollHeight <= container.clientHeight && 
            element.scrollWidth <= container.clientWidth) {
          currentSize = testSize;
        } else {
          element.style.fontSize = `${currentSize}px`;
          break;
        }
        iterationCount++;
        
        if (currentSize >= maxAllowedSize) break;
      }

      const finalSize = `${currentSize}px`;
      
      // Only update if size has changed significantly
      if (Math.abs(currentSize - parseInt(lastSizeRef.current.replace('px', '') || '0')) > 8) {
        setFontSize(finalSize);
        lastSizeRef.current = finalSize;
      }
      
      setIsCalculating(false);
    }, 200); // Longer debounce for better stability
  }, [autoResize, minSize, maxSize, isCalculating]);

  // Dynamic font size adjustment using ResizeObserver
  useEffect(() => {
    if (!autoResize || !textRef.current) return;

    // Initial adjustment with longer delay for stability
    const initialTimeout = setTimeout(() => {
      adjustFontSize();
    }, 300);

    // Set up ResizeObserver for dynamic adjustments
    const resizeObserver = new ResizeObserver(() => {
      adjustFontSize();
    });

    if (textRef.current) {
      resizeObserver.observe(textRef.current);
      const container = textRef.current.parentElement;
      if (container) {
        resizeObserver.observe(container);
      }
    }

    return () => {
      clearTimeout(initialTimeout);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [adjustFontSize]);

  const weightClasses = {
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold'
  };

  const dynamicStyle = autoResize && fontSize ? { fontSize } : {};

  return (
    <div 
      ref={textRef}
      className={cn(
        !autoResize && getSizeClasses(),
        weightClasses[weight],
        "leading-tight", // Removed transitions completely for stability
        shadow && "text-shadow-sm",
        "break-words hyphens-auto overflow-hidden",
        "whitespace-normal",
        className
      )}
      style={dynamicStyle}
      {...(dangerouslySetInnerHTML && { dangerouslySetInnerHTML })}
    >
      {!dangerouslySetInnerHTML && children}
    </div>
  );
};
